import React, {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {Alert, Box, Grid2, Tab, Tabs, Typography} from "@mui/material";
import dayjs from "dayjs";
import {withAuthWrapperComponent} from "@/app/auth/WithAuthWrapperComponent";
import {AppFrame} from "@/app/AppFrame";
import DateInputField from "@/lib/components/inputfields/date-input-field";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {ReportI18N} from "@/translations";
import {reportsApi} from "@/boxhub-api";
import useReportData from "@/pages/reports-admin-page/useReportData";
import useCsvExport from "@/pages/reports-admin-page/useCsvExport";
import RequestsByCompanyReport from "@/pages/reports-admin-page/reports/RequestsByCompanyReport";
import QuotesByCompanyReport from "@/pages/reports-admin-page/reports/QuotesByCompanyReport";
import RequestsByMonthReport from "@/pages/reports-admin-page/reports/RequestsByMonthReport";
import QuotesByMonthReport from "@/pages/reports-admin-page/reports/QuotesByMonthReport";
import RequestStatusByCompanyReport from "@/pages/reports-admin-page/reports/RequestStatusByCompanyReport";
import QuoteStatusByCompanyReport from "@/pages/reports-admin-page/reports/QuoteStatusByCompanyReport";
import CollaborationSummaryReport from "@/pages/reports-admin-page/reports/CollaborationSummaryReport";
import CollaborationByMonthReport from "@/pages/reports-admin-page/reports/CollaborationByMonthReport";
import RequestsDetailReport from "@/pages/reports-admin-page/reports/RequestsDetailReport";
import QuotesDetailReport from "@/pages/reports-admin-page/reports/QuotesDetailReport";
import SupplierRequesterReport from "@/pages/reports-admin-page/reports/SupplierRequesterReport";

const TAB_AGGREGATE = 0;
const TAB_DETAIL = 1;
const TAB_COLLABORATION = 2;

/**
 * Formats a Date to an ISO date string (YYYY-MM-DD).
 */
const toIsoDate = (date: Date): string => dayjs(date).format("YYYY-MM-DD");

/**
 * Management Reports admin page with date range filter and tabbed report views.
 */
const ReportsPage = () => {
    const { t } = useTranslation();

    const defaultFrom = useMemo(() => new Date(new Date().getFullYear(), 0, 1), []);
    const defaultTo = useMemo(() => new Date(), []);

    const [from, setFrom] = useState<Date>(defaultFrom);
    const [to, setTo] = useState<Date>(defaultTo);
    const [activeTab, setActiveTab] = useState(TAB_AGGREGATE);

    const isDateRangeValid = from <= to;

    const fromIso = useMemo(() => (isDateRangeValid ? toIsoDate(from) : ""), [from, isDateRangeValid]);
    const toIso = useMemo(() => (isDateRangeValid ? toIsoDate(to) : ""), [to, isDateRangeValid]);

    const { data, loading } = useReportData(fromIso, toIso);

    const downloadRequestsByCompany = useCsvExport("requests-by-company.csv");
    const downloadQuotesByCompany = useCsvExport("quotes-by-company.csv");
    const downloadRequestsByMonth = useCsvExport("requests-by-month.csv");
    const downloadQuotesByMonth = useCsvExport("quotes-by-month.csv");
    const downloadRequestStatusByCompany = useCsvExport("request-status-by-company.csv");
    const downloadQuoteStatusByCompany = useCsvExport("quote-status-by-company.csv");
    const downloadCollaborationSummary = useCsvExport("collaboration-summary.csv");
    const downloadCollaborationByMonth = useCsvExport("collaboration-by-month.csv");
    const downloadRequestsDetail = useCsvExport("requests-detail.csv");
    const downloadQuotesDetail = useCsvExport("quotes-detail.csv");
    const downloadSupplierRequester = useCsvExport("supplier-requester.csv");

    const handleFromChange = useCallback(({ value }: { name: string; value: unknown }) => {
        if (value instanceof Date) {
            setFrom(value);
        }
    }, []);

    const handleToChange = useCallback(({ value }: { name: string; value: unknown }) => {
        if (value instanceof Date) {
            setTo(value);
        }
    }, []);

    const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    }, []);

    return (
            <AppFrame title={t(ReportI18N.page.title)} loading={loading} padding={2}>
            <Grid2 container direction="column" spacing={3} paddingTop={1}>
                <Grid2>
                    <Grid2 container spacing={2} alignItems="flex-start">
                        <Grid2>
                            <DateInputField
                                name="from"
                                label={t(ReportI18N.page.filter.from)}
                                value={from}
                                onChange={handleFromChange}
                            />
                        </Grid2>
                        <Grid2>
                            <DateInputField
                                name="to"
                                label={t(ReportI18N.page.filter.to)}
                                value={to}
                                onChange={handleToChange}
                            />
                        </Grid2>
                    </Grid2>
                    {!isDateRangeValid && (
                        <Box mt={1}>
                            <Alert severity="error">{t(ReportI18N.page.filter.error)}</Alert>
                        </Box>
                    )}
                </Grid2>

                <Grid2>
                    <Tabs value={activeTab} onChange={handleTabChange}>
                        <Tab label={t(ReportI18N.page.tab.aggregate)}/>
                        <Tab label={t(ReportI18N.page.tab.detail)}/>
                        <Tab label={t(ReportI18N.page.tab.collaboration)}/>
                    </Tabs>
                </Grid2>

                {activeTab === TAB_AGGREGATE && (
                    <Grid2 container direction="column" spacing={4}>
                        <Grid2>
                            <Grid2 container justifyContent="space-between" alignItems="center" mb={1}>
                                <Grid2>
                                    <Typography variant="h6">{t(ReportI18N.page.report.requestsByCompany)}</Typography>
                                </Grid2>
                                <Grid2>
                                    <ButtonEx
                                            label={t(ReportI18N.page.action.export)}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => downloadRequestsByCompany(() => reportsApi.getRequestsByCompanyCsv({ from: fromIso, to: toIso }))}
                                    />
                                </Grid2>
                            </Grid2>
                            <RequestsByCompanyReport data={data.requestsByCompany} />
                        </Grid2>

                        <Grid2>
                            <Grid2 container justifyContent="space-between" alignItems="center" mb={1}>
                                <Grid2>
                                    <Typography variant="h6">{t(ReportI18N.page.report.quotesByCompany)}</Typography>
                                </Grid2>
                                <Grid2>
                                    <ButtonEx
                                            label={t(ReportI18N.page.action.export)}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => downloadQuotesByCompany(() => reportsApi.getQuotesByCompanyCsv({ from: fromIso, to: toIso }))}
                                    />
                                </Grid2>
                            </Grid2>
                            <QuotesByCompanyReport data={data.quotesByCompany} />
                        </Grid2>

                        <Grid2>
                            <Grid2 container justifyContent="space-between" alignItems="center" mb={1}>
                                <Grid2>
                                    <Typography variant="h6">{t(ReportI18N.page.report.requestsByMonth)}</Typography>
                                </Grid2>
                                <Grid2>
                                    <ButtonEx
                                            label={t(ReportI18N.page.action.export)}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => downloadRequestsByMonth(() => reportsApi.getRequestsByMonthCsv({ from: fromIso, to: toIso }))}
                                    />
                                </Grid2>
                            </Grid2>
                            <RequestsByMonthReport data={data.requestsByMonth} />
                        </Grid2>

                        <Grid2>
                            <Grid2 container justifyContent="space-between" alignItems="center" mb={1}>
                                <Grid2>
                                    <Typography variant="h6">{t(ReportI18N.page.report.quotesByMonth)}</Typography>
                                </Grid2>
                                <Grid2>
                                    <ButtonEx
                                            label={t(ReportI18N.page.action.export)}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => downloadQuotesByMonth(() => reportsApi.getQuotesByMonthCsv({ from: fromIso, to: toIso }))}
                                    />
                                </Grid2>
                            </Grid2>
                            <QuotesByMonthReport data={data.quotesByMonth} />
                        </Grid2>

                        <Grid2>
                            <Grid2 container justifyContent="space-between" alignItems="center" mb={1}>
                                <Grid2>
                                    <Typography
                                            variant="h6">{t(ReportI18N.page.report.requestStatusByCompany)}</Typography>
                                </Grid2>
                                <Grid2>
                                    <ButtonEx
                                            label={t(ReportI18N.page.action.export)}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => downloadRequestStatusByCompany(() => reportsApi.getRequestStatusByCompanyCsv({ from: fromIso, to: toIso }))}
                                    />
                                </Grid2>
                            </Grid2>
                            <RequestStatusByCompanyReport data={data.requestStatusByCompany} />
                        </Grid2>

                        <Grid2>
                            <Grid2 container justifyContent="space-between" alignItems="center" mb={1}>
                                <Grid2>
                                    <Typography
                                            variant="h6">{t(ReportI18N.page.report.quoteStatusByCompany)}</Typography>
                                </Grid2>
                                <Grid2>
                                    <ButtonEx
                                            label={t(ReportI18N.page.action.export)}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => downloadQuoteStatusByCompany(() => reportsApi.getQuoteStatusByCompanyCsv({ from: fromIso, to: toIso }))}
                                    />
                                </Grid2>
                            </Grid2>
                            <QuoteStatusByCompanyReport data={data.quoteStatusByCompany} />
                        </Grid2>
                    </Grid2>
                )}

                {activeTab === TAB_DETAIL && (
                    <Grid2 container direction="column" spacing={4}>
                        <Grid2>
                            <Grid2 container justifyContent="space-between" alignItems="center" mb={1}>
                                <Grid2>
                                    <Typography variant="h6">{t(ReportI18N.page.report.requestsDetail)}</Typography>
                                </Grid2>
                                <Grid2>
                                    <ButtonEx
                                            label={t(ReportI18N.page.action.export)}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => downloadRequestsDetail(() => reportsApi.getRequestsDetailCsv({ from: fromIso, to: toIso }))}
                                    />
                                </Grid2>
                            </Grid2>
                            <RequestsDetailReport data={data.requestsDetail} />
                        </Grid2>

                        <Grid2>
                            <Grid2 container justifyContent="space-between" alignItems="center" mb={1}>
                                <Grid2>
                                    <Typography variant="h6">{t(ReportI18N.page.report.quotesDetail)}</Typography>
                                </Grid2>
                                <Grid2>
                                    <ButtonEx
                                            label={t(ReportI18N.page.action.export)}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => downloadQuotesDetail(() => reportsApi.getQuotesDetailCsv({ from: fromIso, to: toIso }))}
                                    />
                                </Grid2>
                            </Grid2>
                            <QuotesDetailReport data={data.quotesDetail} />
                        </Grid2>
                    </Grid2>
                )}

                {activeTab === TAB_COLLABORATION && (
                    <Grid2 container direction="column" spacing={4}>
                        <Grid2>
                            <Grid2 container justifyContent="space-between" alignItems="center" mb={1}>
                                <Grid2>
                                    <Typography
                                            variant="h6">{t(ReportI18N.page.report.collaborationSummary)}</Typography>
                                </Grid2>
                                <Grid2>
                                    <ButtonEx
                                            label={t(ReportI18N.page.action.export)}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => downloadCollaborationSummary(() => reportsApi.getCollaborationSummaryCsv({ from: fromIso, to: toIso }))}
                                    />
                                </Grid2>
                            </Grid2>
                            <CollaborationSummaryReport data={data.collaborationSummary} />
                        </Grid2>

                        <Grid2>
                            <Grid2 container justifyContent="space-between" alignItems="center" mb={1}>
                                <Grid2>
                                    <Typography
                                            variant="h6">{t(ReportI18N.page.report.collaborationByMonth)}</Typography>
                                </Grid2>
                                <Grid2>
                                    <ButtonEx
                                            label={t(ReportI18N.page.action.export)}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => downloadCollaborationByMonth(() => reportsApi.getCollaborationByMonthCsv({ from: fromIso, to: toIso }))}
                                    />
                                </Grid2>
                            </Grid2>
                            <CollaborationByMonthReport data={data.collaborationByMonth} />
                        </Grid2>

                        <Grid2>
                            <Grid2 container justifyContent="space-between" alignItems="center" mb={1}>
                                <Grid2>
                                    <Typography variant="h6">{t(ReportI18N.page.report.supplierRequester)}</Typography>
                                </Grid2>
                                <Grid2>
                                    <ButtonEx
                                            label={t(ReportI18N.page.action.export)}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => downloadSupplierRequester(() => reportsApi.getQuoteSupplierRequesterCsv({ from: fromIso, to: toIso }))}
                                    />
                                </Grid2>
                            </Grid2>
                            <SupplierRequesterReport data={data.supplierRequester} />
                        </Grid2>
                    </Grid2>
                )}
            </Grid2>
        </AppFrame>
    );
};

export default withAuthWrapperComponent(ReportsPage);
